import { brandConfig } from './index';
import { accentInk, darken, hexToRgbTriplet, isValidHex, subBarInk } from './colorHelpers';

/** ブランド (= オリジン) ごとに localStorage を名前空間化する。
 *  localhost では複数ブランドが同一オリジン (localhost:5173) を共有するため、
 *  単一キーだと前に開いていたブランドの背景画像/color_scheme が初期描画で
 *  混入する。VITE_BRAND でキーを分けて衝突を防ぐ (本番は別オリジンなので無影響)。 */
const BRAND_ID = import.meta.env.VITE_BRAND || 'default';
export const PARTNER_BRANDING_CACHE_KEY = `partner_branding:${BRAND_ID}`;

/** 認証前(ログイン/新規登録)画面の背景画像キャッシュ。
 *  post-auth の PARTNER_BRANDING_CACHE は organization スコープで pre-auth では使えないため、
 *  partner code (= ブランド) キーで別管理する。React マウント前に applyBrandTheme() が
 *  即適用し、hard refresh 時の「グラデーション→画像」フラッシュを防ぐ。 */
export const LOGIN_BG_CACHE_KEY = `login_bg:${BRAND_ID}`;

export interface LoginBgCache {
  partner_code?: string | null;
  /** backend が返す安定プロキシ URL (相対パス '/api/partner/bg-image/<id>' 等)。
   *  CSS へ渡す前に resolveBgImageUrl() で絶対化する。NULL なら背景未設定。 */
  login_bg_image_url: string | null;
  /** オンボーディング画面 (auth-pages.css 系) の配色 (partners.onboarding_color_scheme)。
   *  NULL/未指定なら BrandConfig.colorScheme (未指定なら 'dark') を使用。 */
  onboarding_color_scheme?: 'light' | 'dark' | null;
  /** パートナー持ち込み GA4 Measurement ID (partners.ga4_measurement_id)。
   *  設定パートナーのみ値あり。GTM が event:'partner_ga_ready' をトリガーに
   *  ② Config タグを初期化するためのキャッシュ。NULL は未設定/計測停止。 */
  partner_ga_id?: string | null;
}

export function readLoginBgCache(): LoginBgCache | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(LOGIN_BG_CACHE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LoginBgCache;
  } catch {
    return null;
  }
}

function writeLoginBgCache(patch: Partial<LoginBgCache>): void {
  if (typeof localStorage === 'undefined') return;
  const current = readLoginBgCache();
  const next: LoginBgCache = {
    partner_code: current?.partner_code ?? null,
    login_bg_image_url: current?.login_bg_image_url ?? null,
    onboarding_color_scheme: current?.onboarding_color_scheme ?? null,
    partner_ga_id: current?.partner_ga_id ?? null,
    ...patch,
  };
  // partner_ga_id も保持対象に含める。背景・配色が未設定でも GA4 ID だけは
  // 残す必要があるため (背景未設定パートナーで ID が消えるのを防ぐ)。
  if (!next.login_bg_image_url && !next.onboarding_color_scheme && !next.partner_ga_id) {
    localStorage.removeItem(LOGIN_BG_CACHE_KEY);
  } else {
    localStorage.setItem(LOGIN_BG_CACHE_KEY, JSON.stringify(next));
  }
}

/** このページロードで partner_ga_ready を送信済みの GA4 ID (各IDにつき最大1回)。
 *  `A → B → A` の到着順でも A へ再送しないよう Set で管理する。 */
const sentPartnerGaIds = new Set<string>();

/** パートナー持ち込み GA4 の初期化を GTM へ通知する (各IDにつき1回だけ)。
 *  GTM 側は event:'partner_ga_ready' をトリガーに ② Config タグを初期化し、その初回
 *  page_view を送る。SPA 遷移以降の page_view は GA4 Enhanced Measurement に委ねる
 *  (追加の push はしない = 二重計上防止)。id が falsy (未設定/計測停止) なら何もしない。 */
export function firePartnerGaReady(id: string | null | undefined): void {
  if (!id || typeof window === 'undefined') return;
  if (sentPartnerGaIds.has(id)) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'partner_ga_ready', partner_ga_id: id });
  sentPartnerGaIds.add(id);
}

/** login-branding 応答の partner GA4 ID を pre-auth キャッシュへ反映し、GTM へ通知する。
 *  id=null は「未設定/計測停止」= キャッシュをクリアし、dataLayer の partner_ga_id も
 *  明示的に null で上書きして以降の CV 送信を止める。 */
export function applyPartnerGa(id: string | null | undefined): void {
  writeLoginBgCache({ partner_ga_id: id ?? null });
  if (id) {
    firePartnerGaReady(id);
    return;
  }
  // 計測停止/撤去 (API が null を返した): 起動時キャッシュ由来で既に積まれた古い
  // partner_ga_id が dataLayer に残っていると、CV タグ ({{DLV - partner_ga_id}} が
  // 存在する時のみ発火) が旧プロパティへ送信し続ける。null で上書きして CV を止める。
  // (既に初期化済みの gtag による SPA page_view は次回リロードまで残る = 既知の残留。
  //  完全停止が要る場合は ID 撤去検知時にリロードするか、キャッシュ初期化をやめる。)
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ partner_ga_id: null });
}

/** ログイン背景 URL を document root の CSS 変数に反映する (キャッシュも同期)。
 *  url が null のときは変数とキャッシュを明示的に消す (設定解除/未設定パートナーで
 *  古い背景が残らないようにする)。AuthGate の fetch 応答後と起動時の両方で使う。 */
/** 背景画像の上に重ねる暗色オーバーレイ (中央のログインカードの可読性確保用)。
 *  画像がある時だけ適用し、未設定時は透明フォールバックで従来グラデーションを維持する。 */
const LOGIN_BG_OVERLAY = 'linear-gradient(rgba(10, 8, 6, 0.35), rgba(10, 8, 6, 0.6))';
/** ライトモード (colorScheme='light') 用の淡色スクリム。生成り紙トーンで
 *  背景画像を柔らかく覆い、白ベースのログインカードを馴染ませる。 */
const LOGIN_BG_OVERLAY_LIGHT = 'linear-gradient(rgba(246, 243, 238, 0.45), rgba(246, 243, 238, 0.68))';

export function applyLoginBgImage(
  url: string | null,
  partnerCode: string | null | undefined,
  root: HTMLElement = document.documentElement,
): void {
  const resolved = resolveBgImageUrl(url);
  if (resolved) {
    // スクリムは「現在解決済みの認証配色」に追従する (パートナー設定 > BrandConfig)。
    // 呼び出し前に applyAuthScheme() で data-auth-scheme を確定させておくこと。
    const overlay = getAuthScheme(root) === 'light' ? LOGIN_BG_OVERLAY_LIGHT : LOGIN_BG_OVERLAY;
    root.style.setProperty('--brand-login-bg-image', `url(${resolved})`);
    root.style.setProperty('--brand-login-overlay', overlay);
  } else {
    root.style.removeProperty('--brand-login-bg-image');
    root.style.removeProperty('--brand-login-overlay');
  }
  writeLoginBgCache({ partner_code: partnerCode ?? null, login_bg_image_url: url });
}

/** 認証系画面 (ログイン / 新規登録 / オンボーディング〜組織設定) の配色を
 *  <html> の data 属性に反映する。
 *  優先順位: partners.onboarding_color_scheme > BrandConfig.colorScheme > 'dark'。
 *  配色の実体は auth-pages.css の `:root[data-auth-scheme]` と、
 *  useAuthScheme() を参照する AuthGate / AuthPages 系の `.light` 切替。
 *  AuthGate の /partner/login-branding 応答後と起動時 (キャッシュ値) の両方で使う。 */
export function applyAuthScheme(
  scheme: 'light' | 'dark' | null | undefined,
  root: HTMLElement = document.documentElement,
): void {
  const resolved = scheme ?? brandConfig.colorScheme ?? 'dark';
  root.setAttribute('data-auth-scheme', resolved);
  writeLoginBgCache({ onboarding_color_scheme: scheme ?? null });
}

/** 現在適用中の認証系配色を返す (data 属性が未設定ならフォールバック解決)。 */
export function getAuthScheme(root: HTMLElement = document.documentElement): 'light' | 'dark' {
  const attr = root.getAttribute('data-auth-scheme');
  if (attr === 'light' || attr === 'dark') return attr;
  return brandConfig.colorScheme ?? 'dark';
}

export interface ScenarioFieldConfig {
  max_scenario_count: number;
  show_narration: boolean;
  show_dialogue: boolean;
  show_telop: boolean;
}

/** org-admin が編集可能なテーマ色オーバーライド。NULL/未設定なら BrandConfig デフォルトを使用。
 *
 *  3 色のセマンティック:
 *  - **primary_color**: 共有フォルダタブ active / アコーディオン枠 / モーダル枠など、
 *    最も大きい主要面に使う色 (アンビエント寄り)。
 *  - **sub_color**: 個人フォルダタブ active / 生成ステータスバーなど、補助的な面に使う色。
 *  - **accent_color**: 主要 CTA ボタン (.btn-primary / .btn-modal-accent /
 *    .btn-generate-lg) / フォーカスリング / 選択リング / チェックボックスなど、
 *    主導線の強調色。
 */
export interface ThemeOverrides {
  /** プライマリーカラー (大きい主要面用) */
  primary_color?: string | null;
  /** サブカラー (補助面用)。旧キー: accent_color */
  sub_color?: string | null;
  /** アクセントカラー (主要 CTA 用)。旧キー: generation_accent */
  accent_color?: string | null;
}

export interface PartnerBrandingCache {
  /** このキャッシュを取得した組織。テナント跨ぎ誤用防止のためのスタンプ
   *  (API レスポンスには含まれず、保存時にクライアントが付与する)。 */
  organization_id?: string | null;
  /** 所属パートナーの識別子 (例: 'default', 'hypeapp')。
   *  publishable key 未配信時に env key へフォールバックしてよいか判定するのに使う。 */
  partner_code?: string | null;
  /** パートナー専用 Stripe アカウントの publishable key。
   *  NULL の場合 default パートナーのみ VITE_STRIPE_PUBLISHABLE_KEY にフォールバック。 */
  stripe_publishable_key?: string | null;
  bg_image_url: string | null;
  home_hero_title: string | null;
  home_hero_subtitle: string | null;
  /** トップページ背景画像にかかる黒オーバーレイの濃度 (0〜1)。
   *  NULL/未指定なら AppLayout 側のデフォルト (0.2) を使用。 */
  home_overlay_opacity?: number | null;
  /** トップページ「以外」の背景画像にかかる黒オーバーレイの濃度 (0〜1)。
   *  NULL/未指定なら AppLayout 側のデフォルト (0.6) を使用。 */
  home_overlay_opacity_other?: number | null;
  /** トップページのメインカード (.home-gen-card) 背景の不透明度 (0〜1)。
   *  NULL/未指定なら HomePage.css のデフォルト (0.65) を使用。 */
  home_card_opacity?: number | null;
  menu_order: string[] | null;
  scenario_field_config?: ScenarioFieldConfig;
  /** org-admin が設定したテーマ色 override (両キーともオプショナル) */
  theme_overrides?: ThemeOverrides | null;
  /** 背景の明暗に応じた配色 (ホーム下部・フッター等)。
   *  NULL/未指定なら BrandConfig.colorScheme (未指定なら 'dark') を使用。
   *  実体は App.css の `[data-color-scheme]` で定義。 */
  color_scheme?: 'light' | 'dark' | null;
  /** パートナー持ち込み GA4 Measurement ID (partners.ga4_measurement_id)。
   *  NULL/未設定なら partner GA4 へは送信しない。 */
  ga4_measurement_id?: string | null;
}

/**
 * org-admin の theme_overrides を :root に適用する。
 *
 * - primary_color: --brand-primary / -hover / -rgb を上書き
 * - sub_color:     --brand-sub-solid / -hover / --brand-sub-rgb を上書き
 * - accent_color:  --brand-accent-solid / -hover / --brand-accent-rgb を上書き
 *
 * null/未指定の値はスキップ (BrandConfig 既定値を維持)。OrgThemePage のライブ
 * プレビューからも呼び出されるため、副作用を局所化してここに集約。
 */
/** サブカラー背景上の前景色 (ステータスバー用) を CSS 変数に展開する。 */
function applySubBarInk(subHex: string, root: HTMLElement): void {
  const ink = subBarInk(subHex);
  root.style.setProperty('--brand-sub-ink', ink.text);
  root.style.setProperty('--brand-sub-dot-ring', ink.ring);
  root.style.setProperty('--brand-sub-dot-glow', ink.glow);
}

/** アクセントカラー塗りの面 (新規作成ボタンのアイコンチップ等) に乗る前景色を CSS 変数に展開する。 */
export function applyAccentInk(accentHex: string, root: HTMLElement): void {
  root.style.setProperty('--brand-accent-ink', accentInk(accentHex));
}

export function applyThemeOverrides(
  overrides: ThemeOverrides | null | undefined,
  root: HTMLElement = document.documentElement,
): void {
  if (!overrides) return;

  const primary = overrides.primary_color;
  if (primary && isValidHex(primary)) {
    const primaryHover = darken(primary, 0.15);
    const primaryRgb = hexToRgbTriplet(primary);
    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-primary-hover', primaryHover);
    if (primaryRgb) root.style.setProperty('--brand-primary-rgb', primaryRgb);
  }

  const sub = overrides.sub_color;
  if (sub && isValidHex(sub)) {
    const subHover = darken(sub, 0.15);
    const subRgb = hexToRgbTriplet(sub);
    root.style.setProperty('--brand-sub-solid', sub);
    root.style.setProperty('--brand-sub-solid-hover', subHover);
    if (subRgb) root.style.setProperty('--brand-sub-rgb', subRgb);
    applySubBarInk(sub, root);
  }

  const accent = overrides.accent_color;
  if (accent && isValidHex(accent)) {
    const accentHover = darken(accent, 0.15);
    const accentRgb = hexToRgbTriplet(accent);
    root.style.setProperty('--brand-accent-solid', accent);
    root.style.setProperty('--brand-accent-solid-hover', accentHover);
    if (accentRgb) root.style.setProperty('--brand-accent-rgb', accentRgb);
    applyAccentInk(accent, root);
  }
}

/**
 * 背景画像 URL を CSS で使える最終 URL に解決する。
 *
 * backend は `/api/partner/bg-image/<id>` という相対パスを返す (安定 URL = ブラウザが
 * バイトをキャッシュでき、リロード時の背景フラッシュを防ぐ)。フロントとバックエンドが
 * 別オリジンの構成 (VITE_BACKEND_ORIGIN 設定時) では絶対 URL にしないと CSS の url()
 * がフロント側オリジンを指してしまうため、ここでオリジンを前置する。
 *
 * 旧データ互換: localStorage に残った旧来の署名付き絶対 URL (https://...) はそのまま使う。
 */
export function resolveBgImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const origin = import.meta.env.VITE_BACKEND_ORIGIN || '';
  return origin ? `${origin}${url}` : url;
}

export function readPartnerBrandingCache(): PartnerBrandingCache | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(PARTNER_BRANDING_CACHE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PartnerBrandingCache;
  } catch {
    return null;
  }
}

/**
 * ブランド設定に基づくCSS変数を :root に注入する
 * main.tsx のアプリ初期化時に呼び出す
 *
 * 背景画像:
 *   初回訪問時はグラデーションのみ。2 回目以降は localStorage にキャッシュされた
 *   前回 API 取得 URL を即座に適用し「バンドル画像→DB 画像」のフラッシュを回避する。
 */
export function applyBrandTheme(): void {
  const { colors, serviceName, features } = brandConfig;
  const root = document.documentElement;

  // ブランド別スタイルのスコープ用クラス。ポータル要素 (素材フローティングボタン・
  // セレクトのドロップダウン等) は body 直下に出るため、body に付与する。
  document.body.classList.add(`brand-${brandConfig.id}`);

  // usodeki は生成 UI 共通のダークテーマを白ベースに刷新した専用テーマを重ねる
  if (brandConfig.id === 'usodeki') {
    void import('../../styles/usodeki-theme.css');
  }

  root.style.setProperty('--brand-primary', colors.primary);
  root.style.setProperty('--brand-primary-hover', colors.primaryHover);
  root.style.setProperty('--brand-primary-rgb', colors.primaryRgb);
  root.style.setProperty('--brand-sub-solid', colors.subSolid);
  root.style.setProperty('--brand-sub-solid-hover', colors.subSolidHover);
  root.style.setProperty('--brand-sub-rgb', colors.subRgb);
  applySubBarInk(colors.subSolid, root);
  root.style.setProperty('--brand-accent-solid', colors.accentSolid);
  root.style.setProperty('--brand-accent-solid-hover', colors.accentSolidHover);
  root.style.setProperty('--brand-accent-rgb', colors.accentRgb);
  applyAccentInk(colors.accentSolid, root);
  root.style.setProperty('--brand-bg-gradient-start', colors.bgGradientStart);
  root.style.setProperty('--brand-bg-gradient-end', colors.bgGradientEnd);
  // 中間色は任意。未指定ブランドは変数を立てず AppLayout.css のフォールバック
  // (#444444) に委ねる = 従来の見た目のまま。
  if (colors.bgGradientMid) {
    root.style.setProperty('--brand-bg-gradient-mid', colors.bgGradientMid);
  }

  const cached = readPartnerBrandingCache();

  // 背景の明暗に応じた配色 (ホーム下部・フッター等)。実体は App.css の
  // `[data-color-scheme]` で定義。優先順位: パートナー設定 > BrandConfig > 'dark'。
  // (起動時はキャッシュ値を使用。API 応答後の更新は usePartnerBranding 側で行う)
  root.setAttribute(
    'data-color-scheme',
    cached?.color_scheme ?? brandConfig.colorScheme ?? 'dark',
  );

  const cachedBgUrl = resolveBgImageUrl(cached?.bg_image_url);
  if (cachedBgUrl) {
    // 起動時はキャッシュ済み安定 URL を即適用 (React マウント前なので初回ペイントから
    // 背景が乗る)。安定 URL なのでブラウザ HTTP キャッシュにヒットすれば再取得は無し。
    root.style.setProperty('--brand-bg-image', `url(${cachedBgUrl})`);
  } else {
    root.style.removeProperty('--brand-bg-image');
  }

  // 認証系画面の配色 (ログイン/新規登録/オンボーディング)。起動時はキャッシュ値を
  // 即適用し、AuthGate が /partner/login-branding の応答後に applyAuthScheme() で
  // 更新する。背景スクリムがこの配色を参照するため applyLoginBgImage() より先に呼ぶ。
  const loginBg = readLoginBgCache();
  applyAuthScheme(loginBg?.onboarding_color_scheme ?? null, root);

  // 認証前(ログイン/新規登録)画面の背景も、キャッシュ済み URL を起動時に即適用する。
  // AuthGate が /partner/login-branding の応答後に applyLoginBgImage() で更新する。
  applyLoginBgImage(loginBg?.login_bg_image_url ?? null, loginBg?.partner_code, root);

  // パートナー持ち込み GA4: キャッシュ済み ID があれば起動時 (GTM 初期化直後) に
  // partner_ga_ready を通知し、初回 page_view を撃つ (暫定値)。確定値は AuthGate
  // (login-branding) / usePartnerBranding (branding) が応答後に更新する。
  firePartnerGaReady(loginBg?.partner_ga_id ?? null);

  // org-admin が設定したテーマ色 override を適用 (BrandConfig 注入後に上書き)
  applyThemeOverrides(cached?.theme_overrides);

  // ページタイトルをブランド名に設定
  document.title = serviceName;

  // ブランド別 favicon に差し替え (未指定なら index.html の既定値を使う)
  if (brandConfig.favicon) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = brandConfig.favicon;
    link.removeAttribute('type');
  }

  // GTMが無効なブランドではスクリプトを除去
  if (!features.gtm) {
    document.querySelectorAll('script[src*="googletagmanager.com"], noscript iframe[src*="googletagmanager.com"]').forEach(el => {
      el.parentElement?.removeChild(el);
    });
  }
}
